import type { GameState, Route } from './types';
import { aircraftType } from '../data/aircraft';
import { estimateRouteStats, forecastRoute, hubSpecializationEffects } from '../simulation/economy';

export interface OperationDelta {
  weeklyProfitBefore: number;
  weeklyProfitAfter: number;
  cashCostBefore: number;
  cashCostAfter: number;
  loadFactorBefore: number;
  loadFactorAfter: number;
  transferShareBefore: number;
  transferShareAfter: number;
  immediateCashImpact: number;
  risks: string[];
}

function routeDelta(before: Route, after: Route, state: GameState): OperationDelta {
  const beforeStats = estimateRouteStats(state, before);
  const afterStats = estimateRouteStats(state, after);
  const beforeTransfers = beforeStats.passengers > 0 ? beforeStats.transferPassengers / beforeStats.passengers : 0;
  const afterTransfers = afterStats.passengers > 0 ? afterStats.transferPassengers / afterStats.passengers : 0;
  const risks: string[] = [];
  if (afterStats.operatingProfit < beforeStats.operatingProfit) risks.push('预测周利润下降');
  if (afterStats.loadFactor < 0.65) risks.push('预测载客率偏低');
  if (afterStats.unmetDemand > beforeStats.unmetDemand) risks.push('未满足需求增加');
  return { weeklyProfitBefore: beforeStats.operatingProfit, weeklyProfitAfter: afterStats.operatingProfit, cashCostBefore: beforeStats.cashCost, cashCostAfter: afterStats.cashCost, loadFactorBefore: beforeStats.loadFactor, loadFactorAfter: afterStats.loadFactor, transferShareBefore: beforeTransfers, transferShareAfter: afterTransfers, immediateCashImpact: 0, risks };
}

export function forecastHubChange(state: GameState, airportId: string, specialization: Parameters<typeof hubSpecializationEffects>[0]): OperationDelta {
  const hub = state.hubs.find((item) => item.airportId === airportId);
  const routes = state.routes.filter((route) => route.active && [route.originId, route.destinationId].includes(airportId));
  if (!hub || !routes.length) return { weeklyProfitBefore: 0, weeklyProfitAfter: 0, cashCostBefore: 0, cashCostAfter: 0, loadFactorBefore: 0, loadFactorAfter: 0, transferShareBefore: 0, transferShareAfter: 0, immediateCashImpact: 0, risks: ['当前枢纽没有可预测的活跃航线'] };
  const beforeStats = routes.map((route) => estimateRouteStats(state, route));
  const afterState = { ...state, hubs: state.hubs.map((item) => item.airportId === airportId ? { ...item, specialization } : item) };
  const afterStats = routes.map((route) => estimateRouteStats(afterState, route));
  return { weeklyProfitBefore: beforeStats.reduce((sum, item) => sum + item.operatingProfit, 0), weeklyProfitAfter: afterStats.reduce((sum, item) => sum + item.operatingProfit, 0), cashCostBefore: beforeStats.reduce((sum, item) => sum + item.cashCost, 0), cashCostAfter: afterStats.reduce((sum, item) => sum + item.cashCost, 0), loadFactorBefore: beforeStats.reduce((sum, item) => sum + item.loadFactor, 0) / routes.length, loadFactorAfter: afterStats.reduce((sum, item) => sum + item.loadFactor, 0) / routes.length, transferShareBefore: 0, transferShareAfter: 0, immediateCashImpact: 0, risks: specialization === 'premium' ? ['枢纽维护成本和高端服务要求更高'] : specialization === 'lowcost' ? ['高端舱位收益会被压低'] : [] };
}

export function forecastFareChange(state: GameState, routeId: string, fareRatio: number): OperationDelta {
  const route = state.routes.find((item) => item.id === routeId);
  if (!route) throw new Error('没有找到航线');
  return routeDelta(route, { ...route, fareRatio: Math.max(0, fareRatio) }, state);
}

export function forecastAircraftReplacement(state: GameState, routeId: string, fromAircraftId: string, toAircraftId: string): OperationDelta {
  const route = state.routes.find((item) => item.id === routeId);
  const replacement = state.aircraft.find((item) => item.id === toAircraftId);
  if (!route || !replacement) throw new Error('没有找到航线或替换飞机');
  if (aircraftType(replacement.typeId).rangeKm < route.stats.distanceKm) throw new Error('替换飞机航程不足');
  return routeDelta(route, { ...route, aircraftIds: route.aircraftIds.map((id) => id === fromAircraftId ? toAircraftId : id) }, state);
}

export function forecastNewRoute(state: GameState, originId: string, destinationId: string, aircraftId: string): OperationDelta {
  const aircraft = state.aircraft.find((item) => item.id === aircraftId);
  if (!aircraft) throw new Error('没有找到飞机');
  const forecast = forecastRoute(state, originId, destinationId, aircraft);
  return { weeklyProfitBefore: 0, weeklyProfitAfter: forecast.weeklyProfit, cashCostBefore: 0, cashCostAfter: forecast.cashCost, loadFactorBefore: 0, loadFactorAfter: forecast.loadFactor, transferShareBefore: 0, transferShareAfter: 0, immediateCashImpact: -forecast.launchCost, risks: forecast.reasons };
}
